<template>
    <div class="page-background lg:pt-0">
        <div class="lg:h-18 mb-8 flex items-center justify-between bg-watermelon-500 p-3 text-white">
            <div class="ml-auto flex items-center space-x-3">
                <p class="border-r-2 border-white pr-3 text-center">
                    <a class="text-white hover:text-white" :href="route('shop')" title="Change Location" @click.prevent="changeLocation">
                        {{ studio.name }}
                    </a>
                </p>
                <a class="text-white hover:text-white" :href="route('studio.shop.cart.index', { studioSlug: studio.slug })" title="View Cart">
                    <i class="fas fa-fw fa-shopping-cart"></i> ({{ cartQty }})
                </a>
                <a class="text-white hover:text-white" :href="route('studio.shop.checkout', { studioSlug: studio.slug })" title="Checkout"> Checkout </a>
            </div>
        </div>
        <div class="container mx-auto">
            <div class="flex flex-col-reverse lg:flex-row lg:space-x-4">
                <div class="w-full lg:w-1/5">
                    <form ref="searchForm" :action="route('studio.shop.browse', { studioSlug: studio.slug })" method="get">
                        <input
                            id="search"
                            type="search"
                            name="search"
                            :value="search"
                            class="border-dim-gray-300 focus:ring-dim-gray-500 focus:border-dim-gray-500 mb-4 block w-full rounded-md text-sm shadow-sm"
                            title="Search gallery by theme, keywords, or name of the painting"
                            placeholder="Search"
                            aria-label="Search Shop"
                            @keydown.enter="$refs.searchForm.submit()"
                            @search="$refs.searchForm.submit()"
                            @clear="goToShopHome"
                        />
                    </form>
                    <div class="mb-4">
                        <h3 class="my-0 h-12 bg-turquoise-500 p-3 text-2xl text-white"><i class="fas fa-fw fa-th-large"></i> Categories</h3>
                        <ul v-loading="isLoading" class="my-0 border-4 border-t-0 border-white bg-white leading-6 shadow-md">
                            <template v-for="category in categories">
                                <li
                                    :key="`category-${category.id}`"
                                    class="cursor-pointer p-2 pl-4 text-left hover:bg-gray-100 xl:pl-6"
                                    :class="{ 'bg-gray-200': category.id === (selectedCategory || {}).id }"
                                >
                                    <span v-if="category.id === 2" class="float-right" @click="toggleGroups">
                                        <span v-show="showingGroups === false" key="caret-down">
                                            <i class="fas fa-fw fa-caret-down"></i>
                                        </span>
                                        <span v-show="showingGroups === true" key="caret-up">
                                            <i class="fas fa-fw fa-caret-up"></i>
                                        </span>
                                    </span>
                                    <a :href="route('studio.shop.browse', { studioSlug: studio.slug, category: category.id })" class="block w-full">
                                        {{ category.name }}
                                    </a>
                                </li>
                                <li v-if="category.id === 2" :key="`category-${category.id}-groups`" class="px-2">
                                    <transition name="toggle">
                                        <ul v-if="showingGroups">
                                            <li
                                                v-for="group in groups"
                                                :key="`group-${group.id}`"
                                                class="cursor-pointer p-2 pl-6 text-left hover:bg-gray-100 xl:pl-8"
                                                :class="{ 'bg-gray-200': group.id === (selectedGroup || {}).id }"
                                            >
                                                <a :href="route('studio.shop.browse', { studioSlug: studio.slug, category: category.id, group: group.id })" class="block w-full">
                                                    {{ group.name }}
                                                </a>
                                            </li>
                                        </ul>
                                    </transition>
                                </li>
                            </template>
                        </ul>
                    </div>
                </div>
                <div class="mb-4 flex-1">
                    <slot name="title"></slot>
                    <error-messages></error-messages>
                    <div v-if="shared.errors" class="mx-auto mb-4 max-w-2xl rounded-b border-t-4 border-red-500 bg-red-100 px-4 py-3 text-red-900 shadow-md" role="alert">
                        <p class="font-bold leading-normal">Error:</p>
                        <p v-for="(error, index) in shared.errors" :key="`session-errors-${index}`" class="leading-normal">{{ error }}</p>
                    </div>
                    <slot name="errors"></slot>
                    <div v-if="shared.success" class="mx-auto mb-4 max-w-2xl rounded-b border-t-4 border-green-500 bg-green-100 px-4 py-3 text-green-900 shadow-md" role="alert">
                        <p v-for="(success, index) in shared.success" :key="`session-success-${index}`" class="leading-normal">{{ success }}</p>
                    </div>
                    <div
                        v-if="!isLoggedIn()"
                        class="mx-auto mb-4 max-w-2xl rounded-b border-t-4 border-tangerine-500 bg-tangerine-100 px-4 py-3 text-tangerine-900 shadow-md"
                        role="alert"
                    >
                        <p class="mb-0 pr-4">
                            <strong class="font-bold">Warning:</strong>
                            <span class="block sm:inline"
                                >You are not logged in. If you navigate away or close your browser, your cart will not be saved. To save your cart for later,
                                <a :href="route('login')" title="Log In">log in</a> or <a :href="route('register')" title="Create an Account">create an account</a>.</span
                            >
                        </p>
                    </div>
                    <div
                        v-if="(selectedCategory || {}).name === 'Beverages'"
                        class="mx-auto mb-4 max-w-2xl rounded-b border-t-4 border-tangerine-500 bg-tangerine-100 px-4 py-3 text-tangerine-900 shadow-md"
                        role="alert"
                    >
                        <p class="mb-0 pr-4">
                            <strong class="font-bold">Notice:</strong>
                            <span class="block sm:inline"
                                >Alcoholic beverages cannot be taken To-Gogh or Delivered. It is suggested that you call the Studio when you are almost there if you would like your
                                drinks to be ready with your painting(s) or project(s).</span
                            >
                        </p>
                    </div>
                    <slot></slot>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import axios from '@/axios';
    import ErrorMessages from '@/components/ErrorMessages.vue';

    export default {
        name: 'Layout',
        components: {
            ErrorMessages,
        },
        filters: {},
        mixins: [],
        props: {
            studio: {
                required: true,
                type: Object,
            },
            cart: {
                required: true,
                type: Object,
            },
            selectedCategory: {
                type: Object,
                default: () => ({}),
            },
            selectedGroup: {
                type: Object,
                default: () => ({}),
            },
            search: {
                type: String,
                default: '',
            },
        },
        data() {
            return {
                isLoading: true,
                categories: [],
                groups: [],
                showingGroups: false,
            };
        },
        computed: {
            cartQty() {
                return this.cart.items.filter((item) => item.isInStock).reduce((carry, item) => carry + (item.quantity || 1), 0);
            },
        },
        created() {
            if ((this.selectedCategory || {}).id === 2) {
                this.showingGroups = true;
            }
        },
        mounted() {
            this.fetchData();
        },
        methods: {
            fetchData() {
                return Promise.all([
                    axios.get(route('studio.shop.categories', { studioSlug: this.studio.slug })),
                    axios.get(route('studio.shop.groups', { studioSlug: this.studio.slug })),
                ]).then(([categories, groups]) => {
                    this.categories = categories.data.data;
                    this.groups = groups.data.data;
                    this.isLoading = false;
                });
            },
            toggleGroups() {
                this.showingGroups = !this.showingGroups;
            },
            changeLocation(event) {
                if (this.cart.items.length > 0) {
                    this.$confirm('Changing your location will empty your cart. Continue?', 'Warning', {
                        type: 'warning',
                        confirmButtonText: 'Change location',
                        cancelButtonText: 'Nevermind, stay here',
                    }).then(() => {
                        window.location = event.target.href;
                    });
                } else {
                    window.location = event.target.href;
                }
            },
            goToShopHome() {
                window.location = route('studio.shop.index', { studioSlug: this.studio.slug });
            },
        },
    };
</script>

<style>
    .toggle-enter-active {
        -moz-transition-duration: 0.3s;
        -webkit-transition-duration: 0.3s;
        -o-transition-duration: 0.3s;
        transition-duration: 0.3s;
        -moz-transition-timing-function: ease-in;
        -webkit-transition-timing-function: ease-in;
        -o-transition-timing-function: ease-in;
        transition-timing-function: ease-in;
    }

    .toggle-leave-active {
        -moz-transition-duration: 0.3s;
        -webkit-transition-duration: 0.3s;
        -o-transition-duration: 0.3s;
        transition-duration: 0.3s;
        -moz-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
        -webkit-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
        -o-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
        transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
    }

    .toggle-enter-to,
    .toggle-leave {
        max-height: 100px;
        overflow: hidden;
    }

    .toggle-enter,
    .toggle-leave-to {
        overflow: hidden;
        max-height: 0;
    }
</style>
